import { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import { initUseQuery } from "simplydo/core/query";
import {
  Button,
  Divider,
  Label,
  Menu,
  Message,
  Tab,
  TabPane,
} from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import type { ChallengeDetail, IdeaTemplateAssessmentRound } from "../../types";
import YourAssessmentRound from "./YourAssessmentRound";

type IChallengeYourAssessments = {
  challenge: ChallengeDetail;
  demoingTour?: string;
};

function ChallengeYourAssessments({
  challenge,
  demoingTour,
}: IChallengeYourAssessments) {
  const user = useAppSelector((state) => state.user);

  const assessmentRounds =
    challenge.ownerIdeaTemplate?.assessments?.rounds || [];

  const assignedAssessmentQuery = useQuery(
    initUseQuery(api.challenges.getAssignedAssessments, [challenge?._id], {
      isEnabled: !!user?._id,
    }),
  );
  const assignedAssessmentIdeas = assignedAssessmentQuery.data?.ideas;

  const [incompleteAssignments, allAssignments] = useMemo(() => {
    const incompleteAssessmentsPerRound: Record<string, number> = {};
    const assessmentsPerRound: Record<string, number> = {};
    assignedAssessmentIdeas?.forEach((idea) => {
      idea.assessments?.forEach((assessment) => {
        const roundId = assessment.assessmentRoundId || "legacy";

        if (assessment.user !== user?._id) return;
        if (!assessmentsPerRound[roundId]) {
          assessmentsPerRound[roundId] = 0;
        }
        assessmentsPerRound[roundId] += 1;
        if (assessment.isSubmitted) return;
        if (!incompleteAssessmentsPerRound[roundId]) {
          incompleteAssessmentsPerRound[roundId] = 0;
        }
        incompleteAssessmentsPerRound[roundId] += 1;
      });
    });
    return [incompleteAssessmentsPerRound, assessmentsPerRound];
  }, [user?._id, assignedAssessmentIdeas]);

  const { t } = useTranslation();

  return (
    <>
      {!user ?
        <Message>
          <h3>Please log in to view this page</h3>
          <p>
            Register or login to your {util.appName()} account to view your
            assessments for this {t("generic.challenge")}.
          </p>
          <Divider section />
          <Button
            as={Link}
            to={`/login?then=/challenges/${challenge?._id}/assessments`}
            primary
          >
            Login or register
          </Button>
        </Message>
      : <Tab
          panes={assessmentRounds
            .filter(
              (round: IdeaTemplateAssessmentRound) =>
                allAssignments[round.id || "legacy"] &&
                allAssignments[round.id || "legacy"]! > 0,
            )
            .map((round: IdeaTemplateAssessmentRound) => ({
              menuItem: (
                <Menu.Item key={round.id}>
                  {round.name}
                  {incompleteAssignments[round.id] ?
                    <Label circular color="red" size="mini">
                      {incompleteAssignments[round.id]}
                    </Label>
                  : null}
                </Menu.Item>
              ),
              key: round.id,
              render: () => (
                <TabPane key={round.id}>
                  <YourAssessmentRound
                    assessmentRound={round}
                    challenge={challenge}
                    demoingTour={demoingTour}
                  />
                </TabPane>
              ),
            }))}
        />
      }
    </>
  );
}

export default ChallengeYourAssessments;
